// Copyright ©️ 2026 eVolve MEP, LLC

import { useState, useEffect } from 'react';

import type { StrongOmit } from '@evolvemep/api-ts';
import { getDefaultZIndex } from '@mantine/core';

import { WrappedModal } from 'components/Mantine/Modals/WrappedModal';
import { TextInputWrapped } from 'components/Mantine/TextInputWrapped';
import type { CreationModal } from 'components/Mantine/TypeSafeSelect/Creatables/types';

export type CreatableModalProps = {
  title: string;
  opened: boolean;
  onClose: () => void;
  defaultValue: string;
  /** Called with the final value after the user has finished typing. */
  onSubmit: (value: string) => void;
  /** If provided, will be rendered instead of `CreatableModal`.
   * `onSubmit` will not be called; it is expected that this
   * `creationModal` will handle submission.
   */
  creationModal: CreationModal | undefined;
};

const CreatableModalWrapped = ({
  defaultValue,
  onSubmit,
  ...props
}: StrongOmit<CreatableModalProps, 'creationModal'>) => {
  const [value, setValue] = useState(defaultValue);

  useEffect(() => {
    if (props.opened) setValue(defaultValue);
  }, [defaultValue, props.opened]);

  const onConfirm = () => {
    onSubmit(value.trim());
  };

  return (
    <WrappedModal
      size="sm"
      onConfirm={onConfirm}
      disabled={!value.trim()}
      {...props}
      zIndex={getDefaultZIndex('popover') + 1}
      confirmationText="Create"
    >
      <TextInputWrapped autoFocus data-autofocus value={value} onChange={setValue} onSubmit={onConfirm} />
    </WrappedModal>
  );
};

/** Used to get a value from the user when creating a new value via a creatable Select dropdown. */
export const CreatableModal = ({
  creationModal,
  creatable,
  ...props
}: CreatableModalProps & {
  creatable: boolean | undefined;
}) => {
  if (!creatable) return null;
  if (creationModal) return creationModal(props.defaultValue, props);

  return <CreatableModalWrapped {...props} />;
};
